import type {
    DSConfig,
    FixerActions,
    FixerCategoryToFixerVersion,
    FixerNameToVersion,
    FixerVersioningConfig,
    SantaDataFixerPlugin
} from '@wix/document-services-types'
import {FixerCategory, type DataFixerVersioningApi} from '../dataFixerVersioning/dataFixerVersioning'
import _ from 'lodash'
import * as santaDataFixers from '@wix/santa-data-fixer'
import getPageJson from './getPageJson'
import applyFixAndUpdate from './applyFixAndUpdate'
import type {PageExtensionAPI} from '../page'
import type {AnyPageMigrator, BaseMigrator, MasterPageMigrator, PageMigrator} from './dataMigration/dataMigrationRunner'
import type {DocumentManager, LogParams} from '@wix/document-manager-core'

export type FixPageApi = Pick<
    DocumentManager,
    'dal' | 'extensionAPI' | 'pointers' | 'config' | 'experimentInstance' | 'logger'
>

const shouldRerunMigrator = (migrator: BaseMigrator) => migrator.fixerRequiresReruns

const addFixerVersion = (
    fixPageApi: FixPageApi,
    fixerVersions: FixerNameToVersion,
    migrator: BaseMigrator,
    versionOverride?: number
) => {
    const {name, version, disableVersioning} = migrator
    if (_.isFunction(disableVersioning) && disableVersioning?.(fixPageApi as DocumentManager)) {
        return
    }
    fixerVersions[name] = versionOverride ?? version
}

const isExperimentOpen = (value: string) => !!(value && value !== 'old' && value !== 'false')

const getDataFixersParams = (fixPageApi: FixPageApi) => {
    const {extensionAPI, dal, pointers} = fixPageApi
    const {page} = extensionAPI as PageExtensionAPI

    const pageIdsArray = _(dal.getIndexed(page.getPageIndexId('masterPage')).data)
        .filter(data => (data?.type === 'Page' || data?.type === 'AppPage') && data?.id !== 'SITE_STRUCTURE')
        .map('id')
        .value()
    const runningExperiments = dal.get(pointers.getPointer('runningExperiments', 'rendererModel')) ?? {}
    const experiments = _(runningExperiments)
        .pickBy(val => isExperimentOpen(val))
        .keys()
        .value()

    const quickActionsMenuEnabled =
        dal.get(
            pointers.getPointer('siteMetaData', 'rendererModel', {
                innerPath: ['quickActions', 'configuration', 'quickActionsMenuEnabled']
            })
        ) ?? false
    const previewMode = dal.get(pointers.getPointer('previewMode', 'rendererModel'))

    return {
        clientSpecMap: dal.get(pointers.rendererModel.getClientSpecMap()),
        isResponsive: dal.get(pointers.rendererModel.isResponsive()),
        builderComponentModel: dal.get(pointers.rendererModel.isBuilderComponentModel()),
        urlFormatModel: dal.get(pointers.general.getUrlFormat()),
        quickActionsMenuEnabled,
        isViewerMode: !previewMode,
        experiments,
        pageIdsArray
    }
}

export const runPageMigrators = (
    fixPageApi: FixPageApi,
    pageId: string,
    pageMigrators: AnyPageMigrator[],
    pageMigratorsVersioningConfig: FixerVersioningConfig
) => {
    const {extensionAPI, experimentInstance} = fixPageApi
    const fixerVersions: FixerNameToVersion = {}
    const {dataFixerVersioning} = extensionAPI as DataFixerVersioningApi
    const fixerChangesOnReruns: FixerActions = {}

    pageMigrators.forEach(migrator => {
        const fixerExperiment = dataFixerVersioning.decorateExperimentWithVersions(
            experimentInstance,
            {fixerName: migrator.name},
            migrator.version,
            migrator.experimentalVersions
        )

        const migratePage = () => {
            if ('migratePage' in migrator) {
                ;(migrator as PageMigrator).migratePage(fixPageApi as DocumentManager, pageId, fixerExperiment)
            }
            if ('migrateMasterPage' in migrator && pageId === 'masterPage') {
                ;(migrator as MasterPageMigrator).migrateMasterPage(fixPageApi as DocumentManager, fixerExperiment)
            }
        }

        if (
            dataFixerVersioning.hasFixerRunOnCurrentVersion(
                pageId,
                FixerCategory.MIGRATOR,
                migrator.name,
                fixerExperiment.version,
                pageMigratorsVersioningConfig
            )
        ) {
            if (!shouldRerunMigrator(migrator)) {
                return
            }

            const changes = dataFixerVersioning.executeFixerAndSetModifications(
                () => migratePage(),
                pageId,
                migrator.name,
                fixerExperiment.version
            )

            _.merge(fixerChangesOnReruns, changes)
        } else {
            addFixerVersion(fixPageApi, fixerVersions, migrator, fixerExperiment.version)
            migratePage()
        }
    })

    dataFixerVersioning.updatePageVersionData(
        pageId,
        {[FixerCategory.MIGRATOR]: fixerVersions},
        {[FixerCategory.MIGRATOR]: pageMigratorsVersioningConfig}
    )

    dataFixerVersioning.reportFixerActions(FixerCategory.MIGRATOR, fixerChangesOnReruns)
}

export const runSantaDataFixers = async (
    fixPageApi: FixPageApi,
    pageId: string,
    dsConfig: DSConfig,
    santaDataFixerPlugins: SantaDataFixerPlugin[],
    santaDataFixersVersioningConfig: FixerVersioningConfig
) => {
    const {dal, extensionAPI, logger} = fixPageApi
    const {dataFixerVersioning} = extensionAPI as DataFixerVersioningApi

    const fixerVersions: Record<string, FixerCategoryToFixerVersion> = {}
    const fixerChangesOnReruns = {}

    const fixerParams = getDataFixersParams(fixPageApi)

    const fixerFunc = santaDataFixers.fixFactory(
        {getPlugins: () => santaDataFixerPlugins},
        santaDataFixersVersioningConfig
    )
    const fixPage = async (pageJson: any) =>
        await Promise.resolve(
            fixerFunc({
                ...fixerParams,
                pageId,
                pageJson,
                fixerVersions,
                fixerChangesOnReruns,
                captureError: (error: Error, op?: LogParams) => logger.captureError(error, op)
            })
        )

    await applyFixAndUpdate(dal, pageId, getPageJson(pageId, fixPageApi), fixPage)

    Object.keys(fixerVersions).forEach(() => {
        dataFixerVersioning.updatePageVersionData(pageId, fixerVersions[pageId], {
            [FixerCategory.VIEWER]: santaDataFixersVersioningConfig
        })
    })

    dataFixerVersioning.reportFixerActions(FixerCategory.VIEWER, fixerChangesOnReruns)
}
